import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import BasePageLayout from "../../../components/layouts/base-page-layout/base-page-layout";
import EditorPageLayout from "../../../components/layouts/editor-page/editor-page-layout";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import { ConfirmModal } from "../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../hooks/navigation-blocker-hook";
import useValidationGroup from "../../../hooks/validation-group/validation-group-hook";
import currentPortalStore from "../../../stores/current-portal-store";
import { FUNC } from "../../../stores/platform-service-store";
import CreateRmaSettingsCard from "./components/create-rma-settings-card/create-rma-settings-card";
import DiscountCard from "./components/discount-card/discount-card";
import GeneralReturnsSettingsCard from "./components/general-returns-settings-card/general-returns-settings-card";
import ItemsValidationCard from "./components/items-validation-card/items-validation-card";
import PriceDifferenceCalculationsCard
	from "./components/price-different-calculations-card/price-different-calculations-card";
import StoreCreditSettingsCard from "./components/store-credit-settings-card/store-credit-settings-card";
import StoreInformationCard from "./components/store-information-card/store-information-card";
import TrackingFallbackCard from "./components/tracking-fallbacks-card/tracking-fallbacks-card";
import ApiCard from "./components/api-card/api-card";
import useExpandCollapse from "../../../hooks/expand-collapse-hook";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import PortalCheckoutCard from "./components/portal-checkout-card/portal-checkout";
import { FEATURES } from "../../../models/Plan";
import RefundsSettingsCard from "./components/refund-settings-card/refund-settings-card";
import RefundsPreventionsSettingsCard
	from "./components/refund-prevention-settings-card/refund-prevention-settings-card";
import TranslationScope from "../../../translation/translation-scope";
import useTranslation from "../../../translation/hooks/translation-hook";
import A2ARefundsPreventionsSettingsCard
	from './components/a2a-refund-prevention-settings-card/refund-prevention-settings-card';
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";

const StoreSettingsEditorPageComponent: React.FC = observer(() => {
    const { getTranslation } = useDashboardTranslation();
    const { isSaving: isSavingTranslations, save: saveTranslations, hasChanges: hasTranslationChanges } = useTranslation();
    const pageScope = 'dashboard-settings-store';

    useEffect(() => {
        currentPortalStore.load();
    }, []);

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const hasChanges = (): boolean => {
        return currentPortalStore.setting?.hasChanges() || currentPortalStore.currentPortal.hasChanges() || hasTranslationChanges() || false;
    }

    const onSaveClicked = (): void => {
        if (currentPortalStore.setting?.hasChanges()) {
            currentPortalStore.setting?.save();
        }

        if (currentPortalStore.currentPortal.hasChanges()) {
            currentPortalStore.currentPortal.save();
        }

        if (hasTranslationChanges()) {
            saveTranslations();
        }
    }

    const { retryNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const isPriceDifferenceSupported = currentPortalStore.platform?.isFunctionalitySupported(FUNC.FUNC_SETTING_PRICE_DIFFERENCE_VARIANT_EXCHANGE) ||
        dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.PRODUCT_EXCHANGE).isSupported;
    const isInstantExchangeFeatureEnabled = currentPortalStore.isFeatureAllowed(FEATURES.INSTANT_EXCHANGE);
    const isPortalCheckoutFeatureEnabled = currentPortalStore.isFeatureAllowed(FEATURES.PORTAL_CHECKOUT);
    const isAnyToAnyCartFeatureEnabled = currentPortalStore.isFeatureAllowed(FEATURES.ANY_TO_ANY_EXCHANGES_FEATURE);

    const vGroup = useValidationGroup();

    const expandCollapseStore = useExpandCollapse(false);

    return (
        <BasePageLayout
            title={getTranslation('SETTINGS', pageScope)}
            subTitles={[getTranslation('STORE_SETTINGS', pageScope)]}
            instruction={<>
                Configure your store's returns management features and information.
            </>}
        >
            <EditorPageLayout
                toolbar={{
                    hasBack: false,
                    saveButton: {
                        hasChanges: hasChanges(),
                        onClick: onSaveClicked,
                        disabled: !hasChanges() || !vGroup.isValid(),
                        isValid: vGroup.isValid(),
                    },
                    expandAllButton: {
                        expanded: expandCollapseStore.isAllExpanded(),
                        onClick: expandCollapseStore.toggleAllExpanded
                    },
                    isLoading: isSavingTranslations() || currentPortalStore.isLoading || currentPortalStore.currentPortal.isSaving() || currentPortalStore.setting?.isLoading() || currentPortalStore.setting?.isSaving()

                }}>
                <ListLayout>
                    <StoreInformationCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('store_information', v)}
                        validationGroup={vGroup}
                        isExpanded={expandCollapseStore.getIsExpanded('store_information')}
                        dataName="store-information-settings-card"
                    />
                    <GeneralReturnsSettingsCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('general_return_settings', v)}
                        validationGroup={vGroup}
                        isExpanded={expandCollapseStore.getIsExpanded('general_return_settings')}
                        dataName="general-returns-settings-card"
                    />
                    <CreateRmaSettingsCard 
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('create_rma_settings', v)} 
                        isExpanded={expandCollapseStore.getIsExpanded('create_rma_settings')} 
                        vGroup={vGroup}
                        dataName="create-rma-settings-card"
                    />
                    <RefundsSettingsCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('refunds_settings', v)}
                        isExpanded={expandCollapseStore.getIsExpanded('refunds_settings')}
                        dataName="refunds-settings-card"
                    />

                    {isAnyToAnyCartFeatureEnabled ? (
                        <A2ARefundsPreventionsSettingsCard
                            validationGroup={vGroup}
                            onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('refunds_prevention_settings', v)}
                            isExpanded={expandCollapseStore.getIsExpanded('refunds_prevention_settings')}
                            dataName="a2a-refunds-prevention-settings-card"
                        />
                        ) : (
                        <RefundsPreventionsSettingsCard
                            validationGroup={vGroup}
                            onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('refunds_prevention_settings', v)}
                            isExpanded={expandCollapseStore.getIsExpanded('refunds_prevention_settings')}
                            dataName="refunds-prevention-settings-card"
                        />
                    )}

	                <StoreCreditSettingsCard
		                onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('store_credit_settings', v)}
		                isExpanded={expandCollapseStore.getIsExpanded('store_credit_settings')}
		                dataName="store-credit-settings-card"
	                />

                    {isPriceDifferenceSupported && (
                        <PriceDifferenceCalculationsCard
                            onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('price_different_calculations', v)}
                            isExpanded={expandCollapseStore.getIsExpanded('price_different_calculations')}
                            dataName="price-difference-calculations-settings-card"
                        />
                    )}

                    <DiscountCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('discount_card', v)}
                        isExpanded={expandCollapseStore.getIsExpanded('discount_card')}
                        dataName="discount-settings-card"
                    />

	                <PortalCheckoutCard
		                onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('portal_checkout_card', v)}
		                isExpanded={expandCollapseStore.getIsExpanded('portal_checkout_card')}
		                dataName="portal-checkout-settings-card"
	                />

                    <TrackingFallbackCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('tracking_fallback', v)}
                        isExpanded={expandCollapseStore.getIsExpanded('tracking_fallback')}
                        dataName="tracking-fallback-settings-card"
                    />
                    <ItemsValidationCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('items_validation', v)}
                        isExpanded={expandCollapseStore.getIsExpanded('items_validation')}
                        dataName="items-validation-settings-card"
                    />



                    <ApiCard
                        onExpandClick={(v: boolean) => expandCollapseStore.setExpanded('api_card', v)}
                        isExpanded={expandCollapseStore.getIsExpanded('api_card')}
                        dataName="api-settings-card"
                    />
                </ListLayout>
                {isShowUnsaved &&
                    <ConfirmModal
                        cancelButton={{
                            title: 'Cancel',
                            onClick: () => setIsShowUnsaved(false)
                        }}
                        okButton={{
                            title: 'Discard',
                            onClick: () => {
                                currentPortalStore.setting?.discardChanges();
                                currentPortalStore.currentPortal.discardChanges();
                                setIsShowUnsaved(false);
                                retryNavigation();
                            }
                        }}
                    />
                }
            </EditorPageLayout>

        </BasePageLayout>
    );
});

const StoreSettingsEditorPage: React.FC = observer(() => {
    return <TranslationScope scope="request-canceled-email">
        <StoreSettingsEditorPageComponent />
    </TranslationScope>;
});

export default StoreSettingsEditorPage;
